/**
 * Fetcher function for trigger data
 *
 * Extracted to avoid circular dependency between query factories and hooks.
 */

import { fetchWithDefaults } from '@/lib/http/client';

import type { TriggerData, TriggersResponse } from '../core/types';
import { isCrmEventTrigger, isEmailTrigger } from '../core/types';

/**
 * Fetch trigger data for a proposal action.
 *
 * **Throws on any failure — deliberately, and this must not be softened back
 * into a `null` return.** The trigger query caches under the action's ROOT
 * version id while fetching the CURRENT version's id (see `getTriggerActionIds`),
 * so the id used for the request and the slot the result lands in are only
 * loosely coupled. If a failed request resolved to "no triggers" instead of
 * throwing, React Query would treat that as a *successful* empty result and
 * write it into the shared, stable cache slot the live trigger UI reads from —
 * blanking the trigger card for the full `staleTime` even once the real data is
 * current. That is the exact disappearing-trigger-card symptom this area was
 * fixed for, arriving through the back door.
 *
 * Throwing keeps the failure in React Query's error state instead: nothing is
 * cached, the configured retry gets a chance, and both consumers already hide
 * the card on `isError` exactly as they did on `null` — so this is a no-op for
 * what the user sees, and only changes what gets persisted.
 *
 * Note a genuine "this action has no trigger" is NOT a failure: the endpoint
 * answers 200 with sentinel entries, which are filtered out below.
 */
export async function fetchTriggerData(
  orgId: number,
  proposalId: number,
  actionId: number
): Promise<TriggerData[]> {
  const url = `/api/orgs/${orgId}/proposals/${proposalId}/actions/${actionId}/?include=trigger`;

  const response = await fetchWithDefaults(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(
      `Trigger fetch failed for action ${actionId} (HTTP ${response.status})`
    );
  }

  const data = (await response.json()) as TriggersResponse;
  // The endpoint can return non-trigger sentinel entries in this array — e.g.
  // `{ error, code }` when an action has no resolvable trigger (no thread_id,
  // unresolved entity, etc.). Keep only real, renderable triggers so callers
  // never treat a sentinel as a trigger — otherwise the Action Overview shows
  // an expandable chevron whose panel renders nothing.
  return (data.triggers ?? []).filter(
    (trigger): trigger is TriggerData =>
      isEmailTrigger(trigger) || isCrmEventTrigger(trigger)
  );
}
